function buildPromiseDateMessage({
  isPreOrder,
  isAvailable,
  rawBackorder,
  stockNotes,
  shipDateBegin,
  shipDateEnd,
  warehouseStock,
}) {
  if (!window.ShippingEstimate) {
    return "";
  }

  return window.ShippingEstimate.formatText({
    isPreOrder,
    isAvailable,
    rawBackorder,
    stockNotes,
    shipDateBegin,
    shipDateEnd,
    warehouseStock,
  });
}

/**
 * Shared utility class for variant and product management
 */
class ProductVariantUtils {
  static normalizeString(s) {
    return s == null || s === "" ? null : String(s).trim().replace(/\s+/g, " ");
  }

  static getSelections(form) {
    return [0, 1, 2].map((i) => form.querySelector(`input[name="option-${i}"]:checked`)?.value);
  }

  static findMatchingVariant(product, sel) {
    return product.variants.find((v) => {
      const o1 = sel[0] == null || v.option1 == sel[0];
      const o2 = sel[1] == null || v.option2 == sel[1];
      const o3 = sel[2] == null || v.option3 == sel[2];
      return o1 && o2 && o3;
    });
  }

  static existsAvailableVariant(product, sel) {
    const s0 = this.normalizeString(sel[0]);
    const s1 = this.normalizeString(sel[1]);
    const s2 = this.normalizeString(sel[2]);

    return product.variants.some((v) => {
      if (!v.available) return false;
      const v1 = this.normalizeString(v.option1);
      const v2 = this.normalizeString(v.option2);
      const v3 = this.normalizeString(v.option3);
      const o1 = s0 == null || v1 === s0;
      const o2 = s1 == null || v2 === s1;
      const o3 = s2 == null || v3 === s2;
      return o1 && o2 && o3;
    });
  }

  static updateOptionAvailability(form, product, getSelectionsFn) {
    const sel = getSelectionsFn(form).map(this.normalizeString);

    const markGroup = (optIdx) => {
      form.querySelectorAll(`label.variant-label[data-option="${optIdx}"]`).forEach((label) => {
        const val = this.normalizeString(label.dataset.value);
        const canLead = product.variants.some((v) => {
          if (!v.available) return false;
          const vOpt = [this.normalizeString(v.option1), this.normalizeString(v.option2), this.normalizeString(v.option3)];
          for (let i = 0; i < 3; i++) {
            const want = i === optIdx ? val : sel[i];
            if (want != null && vOpt[i] !== want) return false;
          }
          return true;
        });
        label.classList.toggle("is-unavailable", !canLead);
      });
    };

    const hasColor = sel[0] != null;
    const hasSize = sel[1] != null;
    const hasOpt3 = sel[2] != null;

    if (hasColor || hasSize || hasOpt3) {
      markGroup(0);
      markGroup(1);
      markGroup(2);
      return;
    }

    form.querySelectorAll("label.variant-label").forEach((label) => {
      const idx = Number(label.dataset.option);
      const val = this.normalizeString(label.dataset.value);
      const trySel = [...sel];
      trySel[idx] = val;
      const canLead = this.existsAvailableVariant(product, trySel);
      label.classList.toggle("is-unavailable", !canLead);
    });
  }

  static productHasVariablePrice(product) {
    if (!product?.variants?.length) return false;
    const n = (v) => (typeof v.price === "string" ? parseInt(v.price, 10) : v.price) || 0;
    const p0 = n(product.variants[0]);
    for (const v of product.variants) {
      if (n(v) !== p0) return true;
    }
    return false;
  }

  /**
   * B2B quick view: strikethrough from b2b_data metafields (same priority as pdp-buybox / recommended card).
   */
  static getQuickViewB2BCompareCents(variant, variantData) {
    if (!variant) return null;
    const b2bPrice = typeof variant.price === "string" ? parseInt(variant.price, 10) : variant.price;
    const rca = variantData?.b2b_retail_compare_at_cents || 0;
    const rp = variantData?.b2b_retail_cents || 0;
    if (rca > b2bPrice) return rca;
    if (b2bPrice < rp && rp > 0) return rp;
    const cap = variant.compare_at_price;
    if (cap && (typeof cap === "string" ? parseInt(cap, 10) : cap) > b2bPrice) {
      return typeof cap === "string" ? parseInt(cap, 10) : cap;
    }
    return null;
  }

  static setAddToCartState(isAvailable, form) {
    const btn = form?.querySelector("[data-quick-add-btn]");
    const msg = document.querySelector("#quick-view-unavailable-msg");
    if (!btn) return;

    if (isAvailable) {
      btn.disabled = false;
      btn.setAttribute("aria-disabled", "false");
      btn.textContent = "Add to Cart";
      if (msg) msg.classList.add("hide");
      const bis = form.querySelector(".klaviyo-bis-trigger");
      if (bis) bis.classList.add("hide");
      btn.classList.remove("hide");
    } else {
      btn.disabled = true;
      btn.setAttribute("aria-disabled", "true");
      btn.textContent = "Selection Not Available";
      if (msg) msg.classList.remove("hide");
      const bis = form.querySelector(".klaviyo-bis-trigger");
      if (bis) {
        btn.classList.add("hide");
        bis.classList.remove("hide");
      }
    }
  }

  static updatePrice(variant, product) {
    const priceElement = document.querySelector("#quick-view-content [data-qv-product-price]");
    const comparePriceElement = document.querySelector("#quick-view-content [data-qv-compare-price]");
    if (!priceElement || !variant) return;

    const moneyFormat = window.theme?.moneyFormat || "${{amount}}";
    const fmt = (cents) => {
      if (typeof formatMoney === "function") return formatMoney(cents, moneyFormat);
      const n = (Number(cents) / 100).toFixed(2);
      return moneyFormat.replace("{{amount}}", n).replace("${", "$").replace("}", "");
    };

    priceElement.textContent = fmt(variant.price);

    if (comparePriceElement) {
      const b2bEl = document.querySelector("#quick-view-content .quick-view__price[data-b2b-pricing]");
      let compareCents = null;
      if (b2bEl) {
        let variantsData = null;
        const vdj = document.querySelector("#quick-view-content [data-variants-data-json]");
        if (vdj) {
          try {
            variantsData = JSON.parse(vdj.textContent);
          } catch (e) {
            /* ignore */
          }
        }
        const vd = variantsData && variantsData[variant.id];
        compareCents = ProductVariantUtils.getQuickViewB2BCompareCents(variant, vd);
      } else if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        compareCents = variant.compare_at_price;
      }
      if (compareCents != null && compareCents > 0) {
        comparePriceElement.textContent = fmt(compareCents);
        comparePriceElement.style.display = "inline";
      } else {
        comparePriceElement.style.display = "none";
      }
    }
  }
}

/**
 * Shared utility class for vendor notes and promise date calculations
 */
class VendorNotesUtils {
  static updateVendorNotes(variant) {
    let variantsData = {};
    let vendor_notes_input = document.querySelector("#quick-view-content #atc-line-item-props #line-item_vendor-notes");
    let parent_line_properties = document.querySelector("#quick-view-content #atc-line-item-props");

    if (document.querySelector("#quick-view-content [data-variants-data-json]")) {
      variantsData = JSON.parse(document.querySelector("#quick-view-content [data-variants-data-json]").innerHTML);
    }

    if (!Object.keys(variantsData).length || !variant) {
      if (vendor_notes_input && parent_line_properties) {
        parent_line_properties.removeChild(vendor_notes_input);
      }
      return;
    }

    let vendor_notes = variantsData[variant.id]?.["vendor_notes"];
    if (vendor_notes != "" && vendor_notes != null) {
      if (!vendor_notes_input) {
        vendor_notes_input = document.createElement("input");
        vendor_notes_input.id = "line-item_vendor-notes";
        vendor_notes_input.value = vendor_notes;
        vendor_notes_input.name = "properties[_VENDOR Notes]";
        vendor_notes_input.type = "hidden";
        if (parent_line_properties) {
          parent_line_properties.appendChild(vendor_notes_input);
        }
      }
      vendor_notes_input.setAttribute("value", vendor_notes);
    } else {
      if (vendor_notes_input && parent_line_properties) {
        parent_line_properties.removeChild(vendor_notes_input);
      }
    }
  }

  static calculatePromiseDate(variant) {
    let productTags = [];
    if (document.querySelector("#quick-view-content [data-product-tags-json]")) {
      productTags = JSON.parse(document.querySelector("#quick-view-content [data-product-tags-json]").innerHTML);
    }
    const isPreOrder = productTags.some((tag) => tag.toLowerCase() === "pre-order");

    let variantsData = {};
    if (document.querySelector("#quick-view-content [data-variants-data-json]")) {
      variantsData = JSON.parse(document.querySelector("#quick-view-content [data-variants-data-json]").innerHTML);
    }

    const variantData = variantsData[variant.id] || {};
    const shippingData = variantData.variant_shipping || {};
    const warehouseData = variantData.variant_data || {};

    const warehouseStock =
      warehouseData.in_stock_newyork === "true" ||
      warehouseData.in_stock_florida === "true" ||
      warehouseData.in_stock_utah === "true";

    let promiseDate = buildPromiseDateMessage({
      isPreOrder,
      isAvailable: !!variant.available,
      rawBackorder: shippingData.backorder_date,
      stockNotes: shippingData.stock_notes,
      shipDateBegin: parseInt(shippingData.ship_date_begin, 10) || 0,
      shipDateEnd: parseInt(shippingData.ship_date_end, 10) || 0,
      warehouseStock,
    });

    if (!promiseDate.trim()) {
      if (variant.available) {
        promiseDate = "In Stock. Usually ships within 1-2 business days.";
      } else {
        promiseDate = "Out of Stock. Please check back later.";
      }
    }

    return promiseDate;
  }

  static updateLineItemProperties(variant) {
    this.updateVendorNotes(variant);

    const promiseDateInput = document.querySelector("#quick-view-content #atc-line-item-props #line-item_promise-date");
    if (promiseDateInput && variant) {
      let promiseDate = this.calculatePromiseDate(variant);
      promiseDateInput.value = promiseDate;
    }
  }
}

/**
 * Sync parent PDP/cart option selections into cross-sell quick-view drawers.
 */
class CrossSellSync {
  static normalizeOptionName(name) {
    return String(name || "").trim().toLowerCase();
  }

  static getParentOptionsFromPdp() {
    const form = document.querySelector("[data-product-form]");
    if (!form) return {};

    const map = {};
    form.querySelectorAll('input[name^="options["]:checked').forEach((input) => {
      const value = input.value?.trim();
      if (!value) return;
      const match = input.name.match(/^options\[(.+)\]$/);
      if (match) map[match[1]] = value;
    });
    return map;
  }

  static isSkippableCartLine(item) {
    const p = item.properties || {};
    return p._type === "bundle" || (p._type === "base" && p._ts);
  }

  static mapLineItemOptions(item) {
    if (item.options_with_values?.length) {
      const map = {};
      item.options_with_values.forEach((opt) => {
        if (opt.name && opt.value) map[opt.name] = opt.value;
      });
      return map;
    }
    return null;
  }

  static async getParentOptionsFromCart() {
    try {
      const cart = await fetch("/cart.js").then((r) => r.json());
      const parentItem = cart.items.find((item) => !CrossSellSync.isSkippableCartLine(item));
      if (!parentItem) return {};

      const fromLine = CrossSellSync.mapLineItemOptions(parentItem);
      if (fromLine) return fromLine;

      if (parentItem.handle) {
        const product = await fetch(`/products/${parentItem.handle}.js`).then((r) => r.json());
        const variant = product.variants.find((v) => v.id === parentItem.variant_id);
        if (variant && product.options?.length) {
          const map = {};
          product.options.forEach((name, i) => {
            const val = variant[`option${i + 1}`];
            if (val) map[name] = val;
          });
          return map;
        }
      }
    } catch (e) {
      console.error("CrossSellSync: failed to read cart parent options", e);
    }
    return {};
  }

  static findParentValue(parentMap, optionName) {
    if (!parentMap || !optionName) return null;
    if (parentMap[optionName]) return parentMap[optionName];
    const normalized = CrossSellSync.normalizeOptionName(optionName);
    for (const [key, value] of Object.entries(parentMap)) {
      if (CrossSellSync.normalizeOptionName(key) === normalized) return value;
    }
    return null;
  }

  static crossSellHasOptionValue(product, optionIndex, value) {
    if (!product?.variants?.length || value == null) return false;
    const key = `option${optionIndex + 1}`;
    const norm = ProductVariantUtils.normalizeString(value);
    return product.variants.some((v) => ProductVariantUtils.normalizeString(v[key]) === norm);
  }

  static applyOptionValuesToQuickView(form, optionValues, updateUIForSelection) {
    if (!form || !optionValues) return;

    optionValues.forEach((value, idx) => {
      if (!value) return;
      const valueNorm = ProductVariantUtils.normalizeString(value);
      form.querySelectorAll(`input[type="radio"][name="option-${idx}"]`).forEach((radio) => {
        if (ProductVariantUtils.normalizeString(radio.value) === valueNorm) {
          radio.checked = true;
          radio
            .closest(".option-group")
            ?.querySelector(".option-group__title .selected-value")
            ?.replaceChildren(document.createTextNode(radio.value));
          radio
            .closest(".option-group__values")
            ?.querySelectorAll("label.variant-label")
            .forEach((l) => l.classList.remove("checked"));
          radio.closest("label")?.classList.add("checked");
        }
      });
    });

    if (typeof updateUIForSelection === "function") updateUIForSelection();
  }

  static applySelectionsToQuickView(form, product, parentMap, updateUIForSelection) {
    if (!form || !product || !parentMap) return;

    const optionValues = (product.options || []).map((optionName, idx) => {
      const parentVal = CrossSellSync.findParentValue(parentMap, optionName);
      if (!parentVal || !CrossSellSync.crossSellHasOptionValue(product, idx, parentVal)) return null;
      return parentVal;
    });

    CrossSellSync.applyOptionValuesToQuickView(form, optionValues, updateUIForSelection);
  }
}

/**
 * Keeps Complete the Look card thumbnails in sync with the active wood/color selection.
 * PDP and cart hosts are synced independently.
 */
const CrossSellCardImageSync = {
  productCache: new Map(),
  userOverridesByContext: new Map(),

  // Variant JSON exposes the original upload; ask the CDN for the same size the Liquid renders.
  sizedImageUrl(src, width) {
    const url = new URL(src.startsWith("//") ? `https:${src}` : src, window.location.origin);
    url.searchParams.set("width", width);
    url.searchParams.set("format", "webp");
    return url.toString();
  },

  getContextKey(hostEl) {
    if (hostEl?.tagName === "CROSS-SELL-CART-PRODUCTS") return "cart";
    if (hostEl?.tagName === "CROSS-SELL-PRODUCT") return "pdp";
    return "global";
  },

  overrideKey(context, handle) {
    return `${context}:${handle}`;
  },

  setUserOverride(hostEl, handle, selections) {
    if (!handle || !selections?.some(Boolean)) return;
    const context = this.getContextKey(hostEl);
    this.userOverridesByContext.set(this.overrideKey(context, handle), selections);
    hostEl?.lockedHandles?.add(handle);
    hostEl?.savedSelections?.set(handle, selections);
  },

  getUserOverride(hostEl, handle) {
    if (!hostEl || !handle) return null;
    return this.userOverridesByContext.get(this.overrideKey(this.getContextKey(hostEl), handle));
  },

  restoreHostState(hostEl) {
    if (!hostEl?.lockedHandles || !hostEl?.savedSelections) return;
    const prefix = `${this.getContextKey(hostEl)}:`;
    for (const [key, selections] of this.userOverridesByContext) {
      if (!key.startsWith(prefix) || !selections?.some(Boolean)) continue;
      const handle = key.slice(prefix.length);
      hostEl.lockedHandles.add(handle);
      hostEl.savedSelections.set(handle, selections);
    }
  },

  getOptionEntries(product) {
    return (product?.options || [])
      .map((option, idx) => {
        if (typeof option === "string") return {name: option, index: idx};
        if (option && typeof option === "object") {
          return {name: option.name || null, index: option.position != null ? option.position - 1 : idx};
        }
        return {name: null, index: idx};
      })
      .filter((entry) => entry.name);
  },

  buildSelections(product, parentMap) {
    return this.getOptionEntries(product).map(({name, index}) => {
      const parentVal = CrossSellSync.findParentValue(parentMap, name);
      if (!parentVal || !CrossSellSync.crossSellHasOptionValue(product, index, parentVal)) return null;
      return parentVal;
    });
  },

  /**
   * Locked cross-sells keep the user's saved selections; others follow the parent product.
   */
  resolveSelections(hostEl, product, handle, parentMap) {
    const stored = this.getUserOverride(hostEl, handle);
    if (stored?.some(Boolean)) return stored;

    if (hostEl?.lockedHandles?.has(handle)) {
      const saved = hostEl.savedSelections?.get(handle);
      if (saved?.some(Boolean)) return saved;
    }
    return this.buildSelections(product, parentMap);
  },

  variantForSelections(product, selections) {
    if (!product?.variants?.length || !selections?.some(Boolean)) return null;
    const matches = product.variants.filter((v) => {
      const o1 = selections[0] == null || v.option1 === selections[0];
      const o2 = selections[1] == null || v.option2 === selections[1];
      const o3 = selections[2] == null || v.option3 === selections[2];
      return o1 && o2 && o3;
    });
    return matches.find((v) => v.featured_image?.src) || matches[0] || null;
  },

  async loadProduct(handle) {
    if (this.productCache.has(handle)) return this.productCache.get(handle);
    try {
      const product = await fetch(`/products/${handle}.js`).then((r) => r.json());
      this.productCache.set(handle, product);
      return product;
    } catch (e) {
      console.error(`CrossSellCardImageSync: failed to load ${handle}`, e);
      return null;
    }
  },

  applyCardImage(cardEl, variant) {
    if (!cardEl || !variant?.featured_image?.src) return;
    const wrapper = cardEl.querySelector(".responsive-image__wrapper");
    const img = wrapper?.querySelector("img");
    if (!img) return;

    const src = this.sizedImageUrl(variant.featured_image.src, 300);

    wrapper.querySelectorAll("picture source").forEach((source) => source.remove());
    img.setAttribute("src", src);
    img.removeAttribute("srcset");
    img.removeAttribute("data-srcset");
    img.removeAttribute("data-src");
    img.classList.remove("lazyload");
    img.classList.add("lazyloaded");
  },

  async syncHost(hostEl, parentMap) {
    if (!hostEl) return;
    const parent = parentMap instanceof Promise ? await parentMap : parentMap;
    const cards = [...hostEl.querySelectorAll("[data-cross-sell-card]")];
    if (!cards.length) return;

    const cardsByHandle = new Map();
    for (const cardEl of cards) {
      const handle = cardEl.getAttribute("data-product-handle");
      if (!handle) continue;
      if (!cardsByHandle.has(handle)) cardsByHandle.set(handle, []);
      cardsByHandle.get(handle).push(cardEl);
    }

    for (const [handle, cardEls] of cardsByHandle) {
      const product = await this.loadProduct(handle);
      if (!product) continue;

      const selections = this.resolveSelections(hostEl, product, handle, parent);
      if (!selections) continue;

      const variant = this.variantForSelections(product, selections);
      if (!variant) continue;
      cardEls.forEach((cardEl) => this.applyCardImage(cardEl, variant));
    }
  },

  syncCardForHandle(hostEl, handle, selections, product) {
    if (!hostEl || !handle || !selections?.some(Boolean)) return;
    const variant = this.variantForSelections(product, selections);
    if (!variant) return;
    hostEl
      .querySelectorAll(`[data-cross-sell-card][data-product-handle="${handle}"]`)
      .forEach((cardEl) => this.applyCardImage(cardEl, variant));
  },

  scheduleSync(hostEl, parentMapOrGetter) {
    if (!hostEl) return;
    if (hostEl._ctlImageSyncTimer) clearTimeout(hostEl._ctlImageSyncTimer);
    hostEl._ctlImageSyncTimer = setTimeout(() => {
      const parent =
        typeof parentMapOrGetter === "function" ? parentMapOrGetter() : parentMapOrGetter;
      Promise.resolve(parent)
        .then((map) => this.syncHost(hostEl, map))
        .catch((err) => console.error("CrossSellCardImageSync: sync failed", err));
    }, 100);
  },

  syncCartDrawer() {
    requestAnimationFrame(() => {
      setTimeout(() => {
        const host = document.querySelector("cross-sell-cart-products");
        if (!host) return;
        this.syncHost(host, CrossSellSync.getParentOptionsFromCart()).catch((err) => {
          console.error("CrossSellCardImageSync: cart sync failed", err);
        });
      }, 50);
    });
  },
};

/**
 * Shared quick-view helpers for cross-sell drawers.
 */
const CrossSellQuickView = {
  getSelections(form) {
    const names = [...new Set([...form.querySelectorAll('input[type="radio"][name^="option-"]')].map((i) => i.name))];
    return names
      .sort((a, b) => parseInt(a.split("-")[1], 10) - parseInt(b.split("-")[1], 10))
      .map((name) => form.querySelector(`input[type="radio"][name="${name}"]:checked`)?.value);
  },

  attachOptionChangeHandlers(form, product, {onUserOptionChange} = {}) {
    const variantIdInput = document.getElementById("selected-variant-id");
    const mainImage = document.querySelector("#quick-view-image img");
    const getSelectionsFn = (f) => CrossSellQuickView.getSelections(f);

    const applyQuickViewImage = (variant) => {
      if (!mainImage || !variant?.featured_image?.src) return;
      const src = CrossSellCardImageSync.sizedImageUrl(variant.featured_image.src, 524);
      mainImage.setAttribute("src", src);
      mainImage.setAttribute("srcset", "");
    };

    const updateUIForSelection = () => {
      const sel = getSelectionsFn(form);
      const totalOptions = product.options?.length || sel.filter(Boolean).length || 1;
      const fullPicked = sel.filter(Boolean).length === totalOptions;
      const hasPartialSelection = sel.some(Boolean);
      const matched = hasPartialSelection ? CrossSellCardImageSync.variantForSelections(product, sel) : null;

      if (matched) applyQuickViewImage(matched);

      if (fullPicked) {
        const exact = matched;
        if (exact && exact.available) {
          ProductVariantUtils.setAddToCartState(true, form);
          if (variantIdInput) variantIdInput.value = exact.id;
          VendorNotesUtils.updateLineItemProperties(exact);
          ProductVariantUtils.updatePrice(exact, product);
        } else {
          ProductVariantUtils.setAddToCartState(false, form);
          if (variantIdInput && exact) variantIdInput.value = exact.id;
        }
      }

      ProductVariantUtils.updateOptionAvailability(form, product, getSelectionsFn);
    };

    form.querySelectorAll('input[type="radio"]').forEach((radio) => {
      radio.addEventListener("change", (e) => {
        if (e.isTrusted && !e.detail?.programmatic) {
          onUserOptionChange?.();
        }

        radio
          .closest(".option-group")
          ?.querySelector(".option-group__title .selected-value")
          ?.replaceChildren(document.createTextNode(radio.value));

        radio
          .closest(".option-group__values")
          ?.querySelectorAll("label.variant-label")
          .forEach((l) => l.classList.remove("checked"));
        radio.closest("label")?.classList.add("checked");

        updateUIForSelection();
      });
    });

    return {updateUIForSelection, getSelectionsFn};
  },

  bootQuickView(host, parentMap) {
    const productEl = document.querySelector("#quick-view-content #product-info");
    const form = document.getElementById("quick-view-form");
    if (!productEl || !form) return;

    const product = JSON.parse(productEl.innerHTML);
    host.product = product;

    const mainImage = document.querySelector("#quick-view-image img");
    const mainImageSources = document.querySelectorAll("#quick-view-image source");
    for (const imageSource of mainImageSources) imageSource.remove();
    if (mainImage) {
      mainImage.removeAttribute("srcset");
      mainImage.removeAttribute("data-srcset");
      mainImage.removeAttribute("data-src");
    }

    const quickAddBtn = form.querySelector("[data-quick-add-btn]");
    if (quickAddBtn) {
      quickAddBtn.addEventListener("click", (e) => {
        e.preventDefault();
        CartUtils.submitForm(form, quickAddBtn, host.cart);
        host.modal.classList.add("hidden");
        document.body.classList.remove("overflow-hidden");
      });
    }

    const {updateUIForSelection} = CrossSellQuickView.attachOptionChangeHandlers(form, product, {
      onUserOptionChange: () => {
        if (!host.activeHandle) return;
        const selections = CrossSellQuickView.getSelections(form);
        CrossSellCardImageSync.setUserOverride(host, host.activeHandle, selections);
        CrossSellCardImageSync.syncCardForHandle(host, host.activeHandle, selections, product);
      },
    });

    host.updateUIForSelection = updateUIForSelection;

    const storedOverride = host.activeHandle
      ? CrossSellCardImageSync.getUserOverride(host, host.activeHandle)
      : null;
    if (storedOverride?.some(Boolean)) {
      CrossSellCardImageSync.setUserOverride(host, host.activeHandle, storedOverride);
    }

    if (host.activeHandle && host.lockedHandles.has(host.activeHandle)) {
      const saved = host.savedSelections.get(host.activeHandle);
      if (saved?.some(Boolean)) {
        CrossSellSync.applyOptionValuesToQuickView(form, saved, updateUIForSelection);
      } else {
        updateUIForSelection();
      }
    } else if (host.activeHandle) {
      CrossSellSync.applySelectionsToQuickView(form, product, parentMap, updateUIForSelection);
    } else {
      updateUIForSelection();
    }
  },

  async openQuickView(host, pHandle) {
    const html = await fetch(`/products/${pHandle}?view=quick-view`).then((res) => res.text());
    const newEl = document.createElement("div");
    newEl.classList.add("quick-view-content");
    newEl.innerHTML = html;

    host.content.innerHTML = newEl.querySelector("#shopify-section-product-quick-view").innerHTML;
    host.modal.classList.remove("hidden");
    document.body.classList.add("overflow-hidden");
    host.activeHandle = pHandle;

    const parentMap = await host.getParentOptions();
    CrossSellQuickView.bootQuickView(host, parentMap);
  },

  resyncOpenDrawer(host) {
    if (!host.modal || host.modal.classList.contains("hidden")) return;
    if (!host.activeHandle || host.lockedHandles.has(host.activeHandle)) return;

    const form = document.getElementById("quick-view-form");
    if (!form || !host.product || !host.updateUIForSelection) return;

    const parentMap = CrossSellSync.getParentOptionsFromPdp();
    CrossSellSync.applySelectionsToQuickView(form, host.product, parentMap, host.updateUIForSelection);
  },
};

/**
 * Shared utility class for cart operations
 */
class CartUtils {
  static submitForm(form, button, cart) {
    if (!form) return;
    const config = fetchConfig("javascript");
    config.headers["X-Requested-With"] = "XMLHttpRequest";
    delete config.headers["Content-Type"];

    const formData = new FormData();

    const crosssellId = Number(form.querySelector('[name="id"]').value);
    const crosssellQty = Number(form.querySelector('[name="quantity"]').value || 1);

    formData.append("items[0][id]", crosssellId);
    formData.append("items[0][quantity]", crosssellQty);

    const lineItemProps = form.querySelector("#atc-line-item-props");
    if (lineItemProps) {
      const propertyInputs = lineItemProps.querySelectorAll('input[name^="properties["]');
      propertyInputs.forEach((input) => {
        const m = input.name.match(/properties\[(.*?)\]/);
        const propertyName = m && m[1] ? m[1] : null;
        if (propertyName && input.value && input.value.trim() !== "") {
          formData.append(`items[0][properties][${propertyName}]`, input.value);
        }
      });
    }

    // B2B safety net: ensure _Order Type is set even if the form input was missing
    if (window.theme && window.theme.isB2B && !formData.has("items[0][properties][_Order Type]")) {
      formData.append("items[0][properties][_Order Type]", "B2B");
    }

    formData.append("items[0][properties][_type]", "crosssell");

    if (cart) {
      formData.append(
        "sections",
        cart.getSectionsToRender().map((section) => section.id),
      );
      formData.append("sections_url", window.location.pathname);
      cart.setActiveElement(document.activeElement);
    }

    button && (button.innerHTML = "Adding to cart");
    const isCartDrawer = button?.getAttribute("data-is-cart");

    config.body = formData;
    fetch(`${routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: "product-form",
            productVariantId: formData.get("id"),
            errors: response.errors || response.description,
            message: response.message,
          });
          console.log("error = ", response.description);
          return;
        }
        cart?.renderContents(response);
        CrossSellCardImageSync.syncCartDrawer();
      })
      .catch((e) => {
        if (button) button.innerHTML = "Add to cart";
        console.error(e);
        if (isCartDrawer != "1") document.body.classList.remove("overflow-hidden");
      })
      .finally(() => {
        if (button) button.innerHTML = "Add to cart";
        if (cart && cart.classList.contains("is-empty")) cart.classList.remove("is-empty");
        if (isCartDrawer != "1") document.body.classList.remove("overflow-hidden");
      });
  }
}

class CrossSellCartProducts extends HTMLElement {
  constructor() {
    super();
    this.selectBtns = this.querySelectorAll("[data-select-option]");
    this.addToBtns = this.querySelectorAll("[data-add-to-cart]");
    this.cart = this.closest("cart-drawer");
    this.product = null;
    this.isClicked = false;
    this.content = document.getElementById("quick-view-content");
    this.modal = document.getElementById("quick-view-modal");
    this.lockedHandles = new Set();
    this.savedSelections = new Map();
    this.activeHandle = null;
    this.updateUIForSelection = null;
    this.getParentOptions = () => CrossSellSync.getParentOptionsFromCart();

    this.init();
    this.handleAccordions();
  }

  connectedCallback() {
    CrossSellCardImageSync.restoreHostState(this);
    CrossSellCardImageSync.scheduleSync(this, () => CrossSellSync.getParentOptionsFromCart());
  }

  init() {
    this.selectBtns.forEach((button) => {
      button.addEventListener("click", (e) => {
        e.preventDefault();

        const pHandle = button.getAttribute("data-product-id");
        if (pHandle) {
          CrossSellQuickView.openQuickView(this, pHandle).catch((err) => {
            console.error("CrossSellCartProducts: failed to open quick view", err);
          });
        }
      });
    });

    this.addToBtns.forEach((button) => {
      button.addEventListener("click", () => {
        const form = button.closest(".product-item__content")?.querySelector(".recommended-product-form");
        CartUtils.submitForm(form, button, this.cart);
      });
    });

    const closer = document.getElementById("close-quick-view");
    if (closer) {
      closer.addEventListener("click", () => {
        this.modal.classList.add("hidden");
        document.body.classList.remove("overflow-hidden");
      });
    }
  }

  /* -------------------------------
   * Accordion behavior
   * ------------------------------- */

  handleAccordions() {
    const accordionHeadings = this.querySelectorAll(".cart-drawer__cross-sell__tab");
    const accordionContents = this.querySelectorAll(".cart-drawer__cross-sell__content");

    accordionHeadings.forEach((heading) => {
      heading.addEventListener("click", () => {
        const headingTitle = heading.getAttribute("data-title");

        heading.classList.toggle("active");

        accordionContents.forEach((content) => {
          const contentTitle = content.getAttribute("data-title");
          if (contentTitle === headingTitle) {
            content.classList.toggle("active");
          } else {
            content.classList.remove("active");
          }
        });
      });
    });
  }

  updateVendorNotes(variant) {
    let variantsData = {};
    let vendor_notes_input = document.querySelector("#quick-view-content #atc-line-item-props #line-item_vendor-notes");
    let parent_line_properties = document.querySelector("#quick-view-content #atc-line-item-props");

    if (document.querySelector("#quick-view-content [data-variants-data-json]")) {
      variantsData = JSON.parse(document.querySelector("#quick-view-content [data-variants-data-json]").innerHTML);
    }

    if (!Object.keys(variantsData).length || !variant) {
      if (vendor_notes_input && parent_line_properties) {
        parent_line_properties.removeChild(vendor_notes_input);
      }
      return;
    }

    let vendor_notes = variantsData[variant.id]?.["vendor_notes"];
    if (vendor_notes != "" && vendor_notes != null) {
      if (!vendor_notes_input) {
        vendor_notes_input = document.createElement("input");
        vendor_notes_input.id = "line-item_vendor-notes";
        vendor_notes_input.value = vendor_notes;
        vendor_notes_input.name = "properties[_VENDOR Notes]";
        vendor_notes_input.type = "hidden";
        if (parent_line_properties) {
          parent_line_properties.appendChild(vendor_notes_input);
        }
      }
      vendor_notes_input.setAttribute("value", vendor_notes);
    } else {
      if (vendor_notes_input && parent_line_properties) {
        parent_line_properties.removeChild(vendor_notes_input);
      }
    }
  }

  updateLineItemProperties(variant) {
    // Update vendor notes
    this.updateVendorNotes(variant);

    // Update promise date (shipping time)
    const promiseDateInput = document.querySelector("#quick-view-content #atc-line-item-props #line-item_promise-date");
    if (promiseDateInput && variant) {
      let promiseDate = this.calculatePromiseDate(variant);
      promiseDateInput.value = promiseDate;
    }
  }

  calculatePromiseDate(variant) {
    let productTags = [];
    if (document.querySelector("#quick-view-content [data-product-tags-json]")) {
      productTags = JSON.parse(document.querySelector("#quick-view-content [data-product-tags-json]").innerHTML);
    }
    const isPreOrder = productTags.some((tag) => tag.toLowerCase() === "pre-order");

    let variantsData = {};
    if (document.querySelector("#quick-view-content [data-variants-data-json]")) {
      variantsData = JSON.parse(document.querySelector("#quick-view-content [data-variants-data-json]").innerHTML);
    }

    const variantData = variantsData[variant.id] || {};
    const shippingData = variantData.variant_shipping || {};
    const warehouseData = variantData.variant_data || {};

    const warehouseStock =
      warehouseData.in_stock_newyork === "true" ||
      warehouseData.in_stock_florida === "true" ||
      warehouseData.in_stock_utah === "true";

    let promiseDate = buildPromiseDateMessage({
      isPreOrder,
      isAvailable: !!variant.available,
      rawBackorder: shippingData.backorder_date,
      stockNotes: shippingData.stock_notes,
      shipDateBegin: parseInt(shippingData.ship_date_begin, 10) || 0,
      shipDateEnd: parseInt(shippingData.ship_date_end, 10) || 0,
      warehouseStock,
    });

    if (!promiseDate.trim()) {
      if (variant.available) {
        promiseDate = "In Stock. Usually ships within 1-2 business days.";
      } else {
        promiseDate = "Out of Stock. Please check back later.";
      }
    }

    return promiseDate;
  }
}

customElements.define("cross-sell-cart-products", CrossSellCartProducts);

class CrossSellProduct extends HTMLElement {
  constructor() {
    super();
    this.modal = document.getElementById("quick-view-modal");
    this.content = document.getElementById("quick-view-content");
    this.selectBtns = this.querySelectorAll("[data-select-option]");
    this.addToBtns = this.querySelectorAll("[data-add-to-cart]");
    this.cart = document.querySelector("cart-drawer");
    this.tabHeadings = this.querySelectorAll(".product-tab__heading");
    this.tabContents = this.querySelectorAll(".product-tab__content");

    this.product = null;
    this.lockedHandles = new Set();
    this.savedSelections = new Map();
    this.activeHandle = null;
    this.updateUIForSelection = null;
    this.getParentOptions = () => Promise.resolve(CrossSellSync.getParentOptionsFromPdp());

    this.init();
  }

  bindParentOptionListeners() {
    const productForm = document.querySelector("[data-product-form]");
    const resync = () => {
      CrossSellQuickView.resyncOpenDrawer(this);
      CrossSellCardImageSync.scheduleSync(this, () => CrossSellSync.getParentOptionsFromPdp());
    };

    productForm?.addEventListener("change", (e) => {
      if (e.target.matches('input[name^="options["]')) resync();
    });
    document.addEventListener("variant:changed", resync);
  }

  init() {
    this.sliderInit();
    this.bindParentOptionListeners();
    CrossSellCardImageSync.restoreHostState(this);
    CrossSellCardImageSync.scheduleSync(this, () => CrossSellSync.getParentOptionsFromPdp());

    this.selectBtns.forEach((button) => {
      button.addEventListener("click", (e) => {
        e.preventDefault();

        const pHandle = button.getAttribute("data-product-id");
        if (pHandle) {
          CrossSellQuickView.openQuickView(this, pHandle).catch((err) => {
            console.error("CrossSellProduct: failed to open quick view", err);
          });
        }
      });
    });

    this.addToBtns.forEach((button) => {
      button.addEventListener("click", () => {
        const form = button.closest(".product-item__content")?.querySelector(".recommended-product-form");
        CartUtils.submitForm(form, button, this.cart);
      });
    });

    const closeBtn = document.getElementById("close-quick-view");
    if (closeBtn) {
      closeBtn.addEventListener("click", () => {
        this.modal.classList.add("hidden");
        document.body.classList.remove("overflow-hidden");
      });
    }

    const overlay = document.querySelector(".modal-content__overlay");
    if (overlay) {
      overlay.addEventListener("click", () => {
        this.modal.classList.add("hidden");
        document.body.classList.remove("overflow-hidden");
      });
    }

    this.tabHeadings.forEach((heading) => {
      heading.addEventListener("click", (e) => {
        e.preventDefault();
        const title = heading.getAttribute("data-title");

        this.tabHeadings.forEach((rmItem) => rmItem.classList.remove("active"));
        heading.classList.add("active");

        this.tabContents.forEach((content) => {
          const content_title = content.getAttribute("data-title");
          if (content_title === title) {
            content.classList.remove("hidden");
          } else {
            content.classList.add("hidden");
          }
        });
      });
    });
  }

  /* -------------------------------
   * Sliders
   * ------------------------------- */

  /**
   * Builds the prev/next control pair Tiny Slider expects in a controlsContainer.
   * The container is inserted next to the slider so it can be positioned against
   * the surrounding wrapper instead of the slider track itself.
   */
  buildSliderControls(slider) {
    const chevron = (d) =>
      '<svg viewBox="0 0 330.002 330.002" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
      '<path d="' + d + '"/></svg>';

    const left = chevron("M233.25,306.001L127.5,165.005L233.25,24.001c4.971-6.628,3.627-16.03-3-21c-6.627-4.971-16.03-3.626-21,3L96.75,156.005c-4,5.333-4,12.667,0,18l112.5,149.996c2.947,3.93,7.451,6.001,12.012,6.001c3.131,0,6.29-0.978,8.988-3.001C236.878,322.03,238.221,312.628,233.25,306.001z");
    const right = chevron("M233.252,155.997L120.752,6.001c-4.972-6.628-14.372-7.97-21-3c-6.628,4.971-7.971,14.373-3,21l105.75,140.997L96.752,306.001c-4.971,6.627-3.627,16.03,3,21c2.698,2.024,5.856,3.001,8.988,3.001c4.561,0,9.065-2.072,12.012-6.001l112.5-150.004C237.252,168.664,237.252,161.33,233.252,155.997z");

    const controls = document.createElement("div");
    controls.className = "cross-sell-slider__controls";
    controls.innerHTML =
      '<button type="button" aria-label="Previous" class="cross-sell-slider__arrow cross-sell-slider__arrow--prev swiper-button-prev">' + left + "</button>" +
      '<button type="button" aria-label="Next" class="cross-sell-slider__arrow cross-sell-slider__arrow--next swiper-button-next">' + right + "</button>";

    // The arrows sit outside the slider track, so the parent becomes the
    // positioning context they are anchored to.
    slider.parentNode.classList.add("cross-sell-slider__wrap");
    slider.parentNode.insertBefore(controls, slider.nextSibling);
    return controls;
  }

  /**
   * Tiny Slider breakpoints behave like CSS min-width, the opposite of the
   * max-width breakpoints Slick used, so the item counts are declared
   * smallest-first here.
   */
  initSlider(selector, items, responsive) {
    document.querySelectorAll(selector).forEach((slider) => {
      if (!slider.children.length || slider.dataset.sliderInitialized === "true") return;

      slider.dataset.sliderInitialized = "true";

      tns({
        container: slider,
        items: items,
        slideBy: 1,
        gutter: 0,
        loop: false,
        autoplay: false,
        controls: true,
        controlsContainer: this.buildSliderControls(slider),
        nav: false,
        mouseDrag: true,
        preventScrollOnTouch: "auto",
        responsive: responsive,
      });
    });
  }

  sliderInit() {
    this.initSlider("#swiper-list", 1, {
      481: {items: 2},
      769: {items: 3},
      1025: {items: 4},
    });

    this.initSlider("#swiper-group-list", 1.5, {
      481: {items: 2},
      769: {items: 2.5},
      1025: {items: 2.7},
    });
  }
}

customElements.define("cross-sell-product", CrossSellProduct);
